import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiBell,
  FiLock,
  FiInfo,
  FiFileText,
  FiHelpCircle,
  FiTrash2,
  FiChevronRight,
  FiEdit,
} from "react-icons/fi";
import { FaLocationDot } from "react-icons/fa6";
import {
  IoBriefcase,
  IoLogOutOutline,
  IoPersonCircleOutline,
} from "react-icons/io5";
import { HiOutlineShieldCheck } from "react-icons/hi2";
import "./ProfilePage.css";
import { useProfileContext } from "../../context/Profile_context";
import { useLoginContext } from "../../context/Login_context";
import Loader from "../../components/Loader";
import axios from "axios";
import { ACCEPT_HEADER, delete_user_url } from "../../utils/constant";
import Notification from "../../utils/Notification";

// ============CONFIRM-MODAL=========================
const ConfirmModal = ({ type, onCancel, onConfirm }) => {
  const isDelete = type === "delete";
  return (
    <div className="pp-modal-overlay" onClick={onCancel}>
      <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pp-modal-icon">
          {isDelete ? <FiTrash2 size={26} /> : <IoLogOutOutline size={26} />}
        </div>
        <div className="pp-modal-title">
          {isDelete ? "Delete Account?" : "LogOut?"}
        </div>
        <div className="pp-modal-sub">
          {isDelete
            ? "Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently removed."
            : "Are you sure you want to logout from your account?"}
        </div>
        <div className="pp-modal-actions">
          <button className="pp-modal-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button className="pp-modal-confirm" onClick={onConfirm}>
            {isDelete ? "Delete" : "Log Out"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============MENU-ROW=========================
const MenuRow = ({ iconClass, icon, title, subtitle, onClick }) => (
  <div
    className="pp-menu-row"
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => e.key === "Enter" && onClick?.()}
  >
    <div className={`pp-menu-icon-wrap ${iconClass}`}>{icon}</div>
    <div className="pp-menu-text">
      <div className="pp-menu-title">{title}</div>
      <div className="pp-menu-sub">{subtitle}</div>
    </div>
    <FiChevronRight className="pp-menu-arrow" />
  </div>
);

// ==============PROFILE-PAGE=================
const ProfilePage = ({ onLogout }) => {
  const navigate = useNavigate();
  const [modal, setModal] = useState(null);
  const { GetProfileDate, profile_data, profile_loading } = useProfileContext();
  const { setLogout } = useLoginContext();

  useEffect(() => {
    GetProfileDate();
  }, []); // eslint-disable-line
  useEffect(() => {
    if (modal) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "auto";
  }, [modal]);
  const fullName =
    profile_data?.name ||
    `${profile_data?.first_name || ""} ${profile_data?.last_name || ""}`.trim() ||
    "";
  const email = profile_data?.email || "";
  const location = profile_data?.city?.name || profile_data?.location || "";
  const profession = profile_data?.profession || profile_data?.occupation || "";
  const avatarUrl = profile_data?.photo_url || null;
  const initials = fullName
    ? fullName
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "";
  const handleLogout = () => {
    setModal(null);
    setLogout();
  };

  const handleDelete = async () => {
    setModal(null);

    try {
      const Token = localStorage.getItem("token");

      const res = await axios.get(delete_user_url, {
        headers: {
          Accept: ACCEPT_HEADER,
          Authorization: "Bearer " + Token,
        },
      });

      if (res.data.success === 1) {
        Notification("success", res.data.message);

        setLogout();
      } else {
        Notification("error", res.data.message);
      }
    } catch (err) {
      console.log("delete_user_url error", err);
    }
  };
  return (
    <div className="pp-page">
      {/* Header */}
      <header className="pp-header">
        <span className="pp-header-title">Profile</span>

        <button className="pp-bell-btn" aria-label="Notifications">
          <FiBell size={25} />
        </button>
      </header>

      {profile_loading ? (
        <Loader />
      ) : (
        <>
          <div className="pp-content">
            {/* ── Hero Card ── */}
            <div className="pp-hero">
              <div className="pp-avatar-ring">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={fullName} />
                ) : (
                  <div className="pp-avatar-initials">{initials}</div>
                )}
              </div>

              <div className="pp-hero-name">{fullName}</div>
              <div className="pp-hero-email">{email}</div>

              <div className="pp-hero-tags">
                {location ? (
                  <span className="pp-hero-tag">
                    <FaLocationDot size={11} /> {location}
                  </span>
                ) : null}
                {profession ? (
                  <span className="pp-hero-tag">
                    <IoBriefcase size={11} /> {profession}
                  </span>
                ) : null}
              </div>
            </div>

            {/* ── Account Section ── */}
            <div className="pp-section-label">Account</div>

            <div className="pp-menu-card">
              <MenuRow
                iconClass="pp-icon-purple"
                icon={<IoPersonCircleOutline size={20} />}
                title="View My Profile"
                subtitle="See Your Full Profile Details"
                onClick={() => {
                  localStorage.setItem(
                    "myProfile",
                    JSON.stringify(profile_data),
                  );
                  navigate("/myprofile");
                }}
              />
              <MenuRow
                iconClass="pp-icon-purple"
                icon={<FiEdit size={17} />}
                title="Edit Profile"
                subtitle="Update your personal information"
                onClick={() => navigate("/edit-profile")}
              />
              <MenuRow
                iconClass="pp-icon-amber"
                icon={<FiLock size={17} />}
                title="Change Password"
                subtitle="Update your account password"
                onClick={() => {}}
              />
            </div>

            {/* ── App Information Section ── */}
            <div className="pp-section-label">App Information</div>

            <div className="pp-menu-card">
              <MenuRow
                iconClass="pp-icon-blue"
                icon={<FiInfo size={17} />}
                title="About Us"
                subtitle="Learn more about our app"
                onClick={() => {}}
              />
              <MenuRow
                iconClass="pp-icon-green"
                icon={<HiOutlineShieldCheck size={17} />}
                title="Privacy Policy"
                subtitle="View our privacy policy"
                onClick={() => {
                  window.open(
                    "https://erp.applified.co.in/jainam/public/Jainam_Bio_Data_privacypolicy.html",
                    "_blank",
                  );
                }}
              />
              <MenuRow
                iconClass="pp-icon-indigo"
                icon={<FiFileText size={17} />}
                title="Terms & Conditions"
                subtitle="Read terms and conditions"
                onClick={() => {
                  window.open(
                    "https://erp.applified.co.in/jainam/public/Jainam_Bio_Data_privacypolicy.html",
                    "_blank",
                  );
                }}
              />
              <MenuRow
                iconClass="pp-icon-teal"
                icon={<FiHelpCircle size={17} />}
                title="Help & Support"
                subtitle="Get help and contact support"
                onClick={() => {}}
              />
            </div>

            {/* ── Danger Buttons ── */}
            <div className="pp-danger-btns">
              <button
                className="pp-btn-logout"
                onClick={() => setModal("logout")}
                aria-label="Logout"
              >
                <IoLogOutOutline size={24} color="#ef4444" />
                Logout
              </button>

              <button
                className="pp-btn-delete"
                onClick={() => setModal("delete")}
                aria-label="Delete Account"
              >
                <FiTrash2 size={24} color="#ef4444" />
                Delete Account
              </button>
            </div>
          </div>

          {/* ── Confirm Modal ── */}
          {modal && (
            <ConfirmModal
              type={modal}
              onCancel={() => setModal(null)}
              onConfirm={modal === "logout" ? handleLogout : handleDelete}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ProfilePage;
